import { applyRefractionActions } from '@ca/common'
import { useOrderSummary } from '@order/hooks/useOrderSummary'
import { BundleType, OrderFrame, StepMode, StepsState } from '@order/orderTypes'
import { useMemo } from 'react'
import { resolvePd } from '../state/orderState'
import { usePrescription } from '../usePrescription'
import { getOrderValues, withSummaryValues } from './OrderValues'
import useActiveItem from './useActiveItem'
import { useOrderDefaults } from './useOrderDefaults'
import { useOrder } from '../useOrderState'

const useOrderValues = (input: {
  states?: Partial<Record<StepMode, StepsState>>
  frame?: OrderFrame
  bundleType?: BundleType
}) => {
  const order = useOrder()

  const active = useActiveItem(order)
  const bundleType = input.bundleType || active?.bundleType || 'base'
  const defaults = useOrderDefaults(bundleType)
  const frame = input.frame || active?.frame

  const refraction = usePrescription()

  const fullState = useMemo(() => {
    const bundleState = input.states?.bundle || []
    const frameState = input.states?.frame || []

    return [...bundleState, ...frameState]
  }, [input.states])

  const orderValues = useMemo(() => {
    return {
      values: getOrderValues({
        order,
        frame,
        fullState,
        defaults,
        bundle: bundleType,
      }),
      refraction: applyRefractionActions(
        refraction,
        fullState,
        resolvePd(order.pd)
      ),
    }
  }, [refraction, fullState, order, frame, defaults, bundleType])

  const summary = useOrderSummary()

  return useMemo(() => {
    if (summary) {
      return withSummaryValues(orderValues, summary, bundleType)
    }
    return orderValues
  }, [orderValues, summary, bundleType])
}

export default useOrderValues
